import React, { useEffect, useState } from 'react';
import { useUserAccess } from '../hooks/useUserAccess';
import DeleteDocButton from '../components/DeleteDocButton';
import { IconChart, IconDoc, IconLock } from '../components/Icons';
import './PersonalDocs.css';

function RegionalDocs({ user }) {
  const { isAdmin, isDirector, isRegionalManager, isRegional, regionalScope, officeLocation, canUpload, loading: accessLoading } = useUserAccess();
  const [files, setFiles] = useState([]);
  const [managers, setManagers] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // Admin/diretor escolhem qualquer regional. Membro regional ambíguo ('ALL',
  // lotação sem regional cravável) também escolhe — vê todas (decisão do negócio).
  const canSelectLocation = isAdmin || isDirector || (isRegional && regionalScope === 'ALL');

  // Carregar lista de gerentes (para admin/diretor escolher localização)
  useEffect(() => {
    if (canSelectLocation) {
      fetch('/api/listaRegionalManagers')
        .then(res => res.json())
        .then(data => {
          if (!data.error) {
            // Extrair localizações únicas
            const locations = [...new Set(data.managers.map(m => m.officeLocation))].sort();
            setManagers(locations);
          }
        })
        .catch(err => console.error(err));
    }
  }, [canSelectLocation]);

  // Definir localização inicial
  useEffect(() => {
    if (!accessLoading) {
      if (isRegionalManager && !isAdmin && !isDirector) {
        setSelectedLocation(officeLocation || '');
      }
    }
  }, [accessLoading, isRegionalManager, isAdmin, isDirector, officeLocation]);

  // Carregar arquivos
  useEffect(() => {
    if (accessLoading) return;
    if (canSelectLocation && !selectedLocation) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const params = new URLSearchParams({ container: 'docs-gerenciais' });
    if (selectedLocation && canSelectLocation) {
      params.append('location', selectedLocation);
    }

    fetch(`/api/listaDocumentos?${params}`)
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          setError(data.error);
          setFiles([]);
        } else {
          setError(null);
          setFiles(data.files || []);
        }
      })
      .catch(err => setError('Erro ao carregar documentos'))
      .finally(() => setLoading(false));
  }, [selectedLocation, accessLoading, canSelectLocation]);

  const formatFileSize = (bytes) => {
    if (!bytes) return '—';
    const kb = bytes / 1024;
    return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (accessLoading) {
    return <div className="personal-container"><p className="loading-text">Verificando acesso...</p></div>;
  }

  return (
    <div className="personal-container">
      <div className="personal-header">
        <h2><span className="page-icon"><IconChart /></span>Documentos Regionais</h2>
        <p>
          {!canSelectLocation && isRegional && regionalScope === 'ALL' && 'Acesso a todas as regionais'}
          {!canSelectLocation && isRegional && regionalScope && regionalScope !== 'ALL' && `Documentos da ${regionalScope}`}
          {canSelectLocation && 'Acesso a todas as regionais'}
        </p>
      </div>

      {/* Seletor de localização para admin/diretor */}
      {canSelectLocation && (
        <div className="location-selector">
          <label>Selecione a regional:</label>
          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
          >
            <option value="">-- Selecione --</option>
            {managers.map(loc => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <div className="access-denied-card">
          <span className="access-icon"><IconLock /></span>
          <h3>Acesso negado</h3>
          <p>{error}</p>
        </div>
      )}

      {loading ? (
        <p className="loading-text">Carregando documentos...</p>
      ) : (
        <>
          {!selectedFile && !error && (
            <div className="files-list">
              {files.length === 0 && canSelectLocation && !selectedLocation && (
                <p className="empty-message">Selecione uma regional para ver os documentos.</p>
              )}

              {files.length === 0 && selectedLocation && (
                <p className="empty-message">Nenhum documento disponível em {selectedLocation}.</p>
              )}

              {files.map((file, index) => (
                <div key={index} className="file-card" onClick={() => setSelectedFile(file)}>
                  <div className="file-icon"><IconDoc /></div>
                  <div className="file-info">
                    <span className="file-name">{file.name}</span>
                    <span className="file-meta">
                      {formatFileSize(file.size)} • {formatDate(file.lastModified)}
                    </span>
                  </div>
                  <button className="view-button">Visualizar</button>
                  <DeleteDocButton
                    canDelete={canUpload}
                    container="docs-gerenciais"
                    file={file.fullPath || file.name}
                    location={selectedLocation || undefined}
                    onDeleted={() => setFiles(prev => prev.filter((_, i) => i !== index))}
                  />
                </div>
              ))}
            </div>
          )}

          {selectedFile && (
            <div className="pdf-viewer-fullscreen">
              <div className="pdf-viewer-topbar">
                <button className="back-button" onClick={() => setSelectedFile(null)}>
                  ← Voltar
                </button>
                <div className="pdf-viewer-info">
                  <span className="pdf-title">{selectedFile.name}</span>
                  <span className="pdf-subtitle">
                    {selectedLocation ? `Regional ${selectedLocation}` : 'Documentos Regionais'}
                  </span>
                </div>
              </div>

              <iframe
                className="pdf-viewer-frame"
                src={`/api/getDocument?container=docs-gerenciais&file=${encodeURIComponent(selectedFile.fullPath || selectedFile.name)}#toolbar=0&navpanes=0&scrollbar=0`}
                title={selectedFile.name}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default RegionalDocs;